import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { FILE_COLORS } from '../../office/icons';
import { detalleError } from './contexto';
import './editor.css';

const COLOR_EN_BLANCO = FILE_COLORS.form;

/** Miniatura de un formulario: franja del color, título y unas líneas de preguntas. */
function Miniatura({ color }) {
  return (
    <span className="fe-plantilla-mini" style={{ '--fe-mini': color }} aria-hidden="true">
      <span className="fe-plantilla-mini-cabeza" />
      <span className="fe-plantilla-mini-linea fe-plantilla-mini-titulo" />
      <span className="fe-plantilla-mini-linea" />
      <span className="fe-plantilla-mini-opcion" />
      <span className="fe-plantilla-mini-opcion" />
    </span>
  );
}

/**
 * Galería de plantillas ("Nuevo → Formulario" en Drive). Crea el formulario en la carpeta abierta y
 * avisa con `onCreated(archivo)`; quien la usa decide a dónde ir (normalmente, al editor en docs).
 */
export default function GaleriaPlantillas({ open, onOpenChange, folder = '/', onCreated }) {
  const [plantillas, setPlantillas] = useState(null);
  const [creando, setCreando] = useState(null);

  useEffect(() => {
    if (!open || plantillas) return;
    let vivo = true;
    api.get('/forms/plantillas')
      .then(({ data }) => vivo && setPlantillas(Array.isArray(data) ? data : []))
      .catch(() => vivo && setPlantillas([]));
    return () => { vivo = false; };
  }, [open, plantillas]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    // "En blanco" ya tiene su tarjeta grande arriba
    (plantillas || []).filter((p) => p.id !== 'blanco').forEach((p) => {
      const cat = p.categoria || 'Otras';
      if (!mapa.has(cat)) mapa.set(cat, []);
      mapa.get(cat).push(p);
    });
    return [...mapa.entries()];
  }, [plantillas]);

  const crear = async (plantilla) => {
    if (creando) return;
    setCreando(plantilla || 'blanco');
    try {
      const { data } = await api.post('/forms', { folder, ...(plantilla ? { plantilla } : {}) });
      onCreated?.(data.archivo);
    } catch (err) {
      toast.error(detalleError(err, 'No se pudo crear el formulario'));
    } finally {
      setCreando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !creando && onOpenChange(v)}>
      <DialogContent className="fe-dialogo fe-galeria max-w-3xl rounded-2xl">
        <DialogHeader>
          <DialogTitle>Nuevo formulario</DialogTitle>
          <DialogDescription>Empieza en blanco o con una plantilla y cámbiala a tu gusto.</DialogDescription>
        </DialogHeader>
        <div className="fe-galeria-cuerpo fm-scroll">
          <div className="fe-galeria-rejilla">
            <button type="button" className="fe-plantilla" onClick={() => crear(null)} disabled={!!creando}>
              <span className="fe-plantilla-blanco" style={{ '--fe-mini': COLOR_EN_BLANCO }}>
                {creando === 'blanco' ? <Loader2 className="od-spin" /> : <Plus />}
              </span>
              <span className="fe-plantilla-nombre">Formulario en blanco</span>
            </button>
          </div>
          {plantillas === null && <div className="fe-centro"><Loader2 className="od-spin" /></div>}
          {grupos.map(([categoria, lista]) => (
            <section key={categoria} className="fe-galeria-grupo">
              <h3>{categoria}</h3>
              <div className="fe-galeria-rejilla">
                {lista.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="fe-plantilla"
                    onClick={() => crear(p.id)}
                    disabled={!!creando}
                    title={p.descripcion || p.nombre}
                  >
                    <span className="fe-plantilla-marco">
                      <Miniatura color={p.color || COLOR_EN_BLANCO} />
                      {creando === p.id && <span className="fe-plantilla-cargando"><Loader2 className="od-spin" /></span>}
                      {p.es_cuestionario && <span className="fe-plantilla-etiqueta">Cuestionario</span>}
                    </span>
                    <span className="fe-plantilla-nombre">{p.nombre}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
